import * as React from 'react';
import {
  Box,
  IconButtonProps,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { NotWorthIt } from '../../../types/util';
import DoNotStripSpacesSpan from '../../common/DoNotStripSpacesSpan';
import usePaginatedDocuments from '../../hooks/usePaginatedDocuments';
import useGetDocumentsWithMarks from '../../hooks/useGetDocumentsWithMarks';
import TrainingSetDocumentActions from './TrainingSetDocumentActions';
import { AppContext } from '../../../App';
import { ITrainingSet } from '../../../types/my-types';
import { format } from 'date-fns';
import TableButtonsHeader from '../../common/TableButtonsHeader';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import { LinkTableStyled } from '../../common/StyledComponents';
import { compactTableStyles } from '../../common/tableStyles';

export interface ITrainingSetDocumentListProps {
  trainingSet: ITrainingSet;
  dpid: string;
}

const TrainingSetDocumentList: React.FC<ITrainingSetDocumentListProps> = ({ trainingSet, dpid }) => {
  const { setToast } = React.useContext(AppContext);
  const [page, setPage] = React.useState(0);
  const [perPage, setPerPage] = React.useState(5);

  const {
    data,
    loading: isLoading,
    previousPage,
    nextPage,
    isFetching,
  } = usePaginatedDocuments({
    group: 'train',
    groupId: trainingSet.id,
    limit: perPage,
    dpId: dpid,
  });

  let marks = useGetDocumentsWithMarks(data?.results || [], trainingSet.id);

  return (
    <>
      <TableButtonsHeader>
        <Typography style={{ fontWeight: 'bold' }}>
          Documents{' '}
          <span style={{ color: '#65BBA9' }} data-cy="training-document-count">
            ({data?.count})
          </span>
        </Typography>
      </TableButtonsHeader>
      <Box sx={compactTableStyles.containerSx}>
        <Table sx={compactTableStyles.tableSx}>
          <TableHead sx={compactTableStyles.tableHeadSx}>
            <TableRow>
              <TableCell align="left">
                Document ID
              </TableCell>
              <TableCell align="left">
                Document
              </TableCell>
              <TableCell align="center">
                Created At
              </TableCell>
              <TableCell align="center">
                # of Marks
              </TableCell>
              <TableCell align="center">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody data-cy="training-document-tablelist-tbody" sx={compactTableStyles.tableBodySx}>
            {isLoading
              ? Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton animation="wave" variant="rounded" height={14} width={56} sx={{ borderRadius: '4px' }} /></TableCell>
                    <TableCell><Skeleton animation="wave" variant="rounded" height={14} width={[200, 180, 220, 190, 210][i]} sx={{ borderRadius: '4px' }} /></TableCell>
                    <TableCell align="center"><Skeleton animation="wave" variant="rounded" height={14} width={88} sx={{ borderRadius: '4px', mx: 'auto' }} /></TableCell>
                    <TableCell align="center"><Skeleton animation="wave" variant="rounded" height={14} width={32} sx={{ borderRadius: '4px', mx: 'auto' }} /></TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                        <Skeleton animation="wave" variant="circular" width={18} height={18} />
                        <Skeleton animation="wave" variant="circular" width={18} height={18} />
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              : null}

            {!isLoading && !isFetching && data?.results.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5}>
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '10px' }}>
                    No documents found.
                  </div>
                </TableCell>
              </TableRow>
            ) : null}
            {!isLoading &&
              data?.results?.map((doc: NotWorthIt, index: number) => {
                const markCountForDoc = marks?.find((mark) => mark.documentId === doc.id);
                return (
                  <TableRow key={doc.id}>
                    <TableCell component="th" scope="row">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <LinkTableStyled to={`/data-pools/${dpid}/documents/${doc.id}`}>
                          {doc.id}
                        </LinkTableStyled>

                        <span
                          onClick={() => {
                            navigator.clipboard.writeText(doc.id || '');
                            setToast({
                              open: true,
                              message: 'Copied!',
                              variant: 'success',
                            });
                          }}
                        >
                          <ContentCopyIcon style={{ fontSize: '14px', cursor: 'pointer' }} />
                        </span>
                      </Box>
                    </TableCell>
                    <TableCell component="th" scope="row">
                      <DoNotStripSpacesSpan
                        title={doc.label}
                        cypressId={`lbl-ts-doc-${doc.label}`}
                      />
                    </TableCell>
                    <TableCell align="center" data-cy="training-set-document-created">
                      {format(new Date(doc!.created_at), 'yyyy-MM-dd HH:mm:ss')}
                    </TableCell>

                    <TableCell align="center">
                      {marks && markCountForDoc ? markCountForDoc.count : 0}
                    </TableCell>
                    <TableCell align="right">
                      <TrainingSetDocumentActions document={doc} trainingSet={trainingSet} />
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
        {(data?.count || 0) > 0 && <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50, 150]}
          component="div"
          count={data?.count || 0}
          rowsPerPage={perPage}
          page={page}
          backIconButtonProps={
            {
              'aria-label': 'Previous Page',
              'data-cy': 'btn-prev-page',
            } as IconButtonProps & { 'data-cy': string }
          }
          nextIconButtonProps={
            {
              'aria-label': 'Next Page',
              'data-cy': 'btn-next-page',
            } as IconButtonProps & { 'data-cy': string }
          }
          onPageChange={(e, p) => {
            e?.preventDefault();
            if (p > page) {
              nextPage();
              setPage(p);
            } else {
              previousPage();
              setPage(p);
            }
          }}
          onRowsPerPageChange={(e: any) => {
            e?.preventDefault();
            setPerPage(e.target.value);
          }}
        />}
      </Box>
    </>
  );
};

export default TrainingSetDocumentList;
